/* Demo NDA — mutual confidentiality + IP-protection notice for anyone
   evaluating verqo via the public /demo link. Linked from the demo
   acknowledgement modal and from the demo landing footer. */
import { Link } from "react-router-dom";
import { Lock, ArrowLeft } from "lucide-react";

export default function DemoNda() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="legal-demo-nda">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/demo" className="text-xs text-foreground/55 hover:text-foreground inline-flex items-center gap-1.5 mb-8">
          <ArrowLeft className="w-3 h-3"/>Back to demo
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-foreground text-accent grid place-items-center">
            <Lock className="w-5 h-5"/>
          </div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">Legal · verqo Wholesale OS</div>
        </div>
        <h1 className="display text-4xl tracking-tight mt-2">Demo &amp; Evaluation NDA</h1>
        <div className="text-[12px] text-foreground/55 mt-2">v1.0 · Effective Feb 1, 2026</div>

        <div className="mt-8 prose prose-sm max-w-none text-foreground/85 leading-relaxed">
          <p>
            By launching, viewing, or otherwise interacting with the verqo
            demo environment ("Demo") you ("Recipient") agree to the terms of
            this mutual non-disclosure agreement ("Agreement") with verqo,
            Inc. ("verqo"). This Agreement is binding the moment you click
            "Acknowledge &amp; continue" on the demo landing or otherwise
            access the Demo.
          </p>

          <h2 className="display text-xl mt-8 mb-2">1. Confidential Information</h2>
          <p>
            "Confidential Information" includes the entire Demo environment
            and everything Recipient sees, hears, infers, screenshots, or
            otherwise observes during their evaluation, including but not
            limited to:
          </p>
          <ul>
            <li>The Platform's user interface, layout, copy, navigation, animations, and interaction design;</li>
            <li>Workflow logic, business rules, scoring methodology, AI underwriting prompts and outputs, and credit-decision flows;</li>
            <li>Architecture, integrations, vendor list, data models, pricing tiers, and roadmap;</li>
            <li>Source-code structure (visible via developer tools), API surface, and any non-public OpenAPI documentation;</li>
            <li>Sample data shown in the Demo (even when fictional, the schema and relationships are proprietary);</li>
            <li>Any pricing, commercial terms, or follow-on offers discussed during evaluation.</li>
          </ul>
          <p>
            Confidential Information does <strong>not</strong> include
            information that is (a) lawfully in Recipient's possession before
            disclosure, (b) becomes public through no fault of Recipient,
            (c) is independently developed without reference to the Demo, or
            (d) is rightfully received from a third party without confidentiality obligation.
          </p>

          <h2 className="display text-xl mt-8 mb-2">2. Permitted use</h2>
          <p>
            Recipient may use the Demo solely for the purpose of internal
            evaluation of whether to commercially engage verqo. No other use
            is permitted. In particular, Recipient may not:
          </p>
          <ul>
            <li><strong>Reproduce, copy, clone, or substantially imitate</strong> any portion of the Platform's UI, copy, navigation, or interaction patterns in any product, prototype, deck, mock-up, or written material;</li>
            <li><strong>Reverse engineer, decompile, disassemble</strong> the Platform, its frontend, backend, AI prompts, scoring weights, or data models;</li>
            <li><strong>Scrape, crawl, frame, mirror, or systematically extract</strong> any content from the Demo;</li>
            <li><strong>Train, fine-tune, or evaluate</strong> any AI/ML model, dataset, or competitive-intelligence product on Demo content;</li>
            <li><strong>Run automated agents, bots, or load tests</strong> against the Demo;</li>
            <li><strong>Probe or test the security</strong> of the Demo or attempt to bypass any rate limit, authentication, or access control (responsible disclosures via <a href="mailto:security@traqo.com" className="underline">security@traqo.com</a> are welcomed);</li>
            <li><strong>Share screenshots, recordings, or written descriptions</strong> of the Platform with anyone outside Recipient's evaluation team without verqo's prior written consent;</li>
            <li><strong>Publicly disclose</strong> the existence of verqo features, integrations, or pricing prior to verqo's public launch of the same.</li>
          </ul>

          <h2 className="display text-xl mt-8 mb-2">3. Trade secrets</h2>
          <p>
            Recipient acknowledges that the Platform's underwriting model,
            scoring weights, AI prompt library, integration topology, fraud
            heuristics, pricing logic, and roadmap constitute verqo's trade
            secrets under the Defend Trade Secrets Act (18 U.S.C. § 1836)
            and applicable state law. Misappropriation of trade secrets
            entitles verqo to injunctive relief, exemplary damages, and
            attorneys' fees.
          </p>

          <h2 className="display text-xl mt-8 mb-2">4. Intellectual property</h2>
          <p>
            All right, title, and interest in the Platform — including all
            code, designs, copy, trademarks, processes, and aggregate
            insights derived from operating it — remain verqo's exclusive
            property. Nothing in this Agreement transfers or licenses any
            such rights to Recipient. Any feedback Recipient provides may be
            used by verqo without restriction or compensation.
          </p>

          <h2 className="display text-xl mt-8 mb-2">5. Term</h2>
          <p>
            This Agreement is effective on the date of acknowledgement and
            continues for <strong>three (3) years</strong> thereafter, except
            that obligations with respect to trade secrets continue for as
            long as such information remains a trade secret under applicable law.
          </p>

          <h2 className="display text-xl mt-8 mb-2">6. No warranty · No production rights</h2>
          <p>
            The Demo is provided "AS IS" with no warranties of any kind.
            Recipient is granted a non-exclusive, non-transferable,
            revocable, time-limited evaluation licence — not a production
            licence. The data shown is fictional. No real money moves; no
            credit decisions made in the Demo create any real-world
            obligation. verqo disclaims all liability arising from
            Recipient's use of the Demo.
          </p>

          <h2 className="display text-xl mt-8 mb-2">7. Privacy</h2>
          <p>
            verqo logs Recipient's name, business email, company (if
            provided), IP address, and timestamp at the moment of
            acknowledgement, plus standard request metadata while Recipient
            uses the Demo. This data is retained for audit and contract
            enforcement and is governed by our{" "}
            <Link to="/legal/privacy" className="underline">Privacy Policy</Link>.
            Demo activity is not used to populate any production credit decision.
          </p>

          <h2 className="display text-xl mt-8 mb-2">8. Injunctive relief &amp; remedies</h2>
          <p>
            Recipient acknowledges that breach of this Agreement would cause
            irreparable harm not adequately compensable in damages, and that
            verqo is entitled to seek injunctive relief, specific
            performance, and any other equitable remedy without the
            requirement to post bond, in addition to monetary damages and
            attorneys' fees.
          </p>

          <h2 className="display text-xl mt-8 mb-2">9. Governing law &amp; venue</h2>
          <p>
            This Agreement is governed by the laws of the State of Delaware
            without regard to conflict-of-laws principles. Exclusive venue
            for any dispute is the state and federal courts located in
            Delaware, except that verqo may seek injunctive relief in any
            court of competent jurisdiction to protect its trade secrets and
            intellectual property.
          </p>

          <h2 className="display text-xl mt-8 mb-2">10. Miscellaneous</h2>
          <ul>
            <li><strong>Entire agreement</strong>: this Agreement is the complete agreement between the parties regarding the Demo.</li>
            <li><strong>No assignment</strong>: Recipient may not assign this Agreement without verqo's written consent.</li>
            <li><strong>Severability</strong>: if any provision is held unenforceable, the remainder stays in force.</li>
            <li><strong>Electronic acknowledgement</strong>: Recipient agrees that clicking "Acknowledge &amp; continue" is a legally binding signature under the federal ESIGN Act (15 U.S.C. § 7001) and equivalent state law.</li>
          </ul>

          <h2 className="display text-xl mt-8 mb-2">Contact</h2>
          <p>
            Questions: <a href="mailto:legal@verqo.com" className="underline">legal@verqo.com</a>.
            Security disclosures: <a href="mailto:security@traqo.com" className="underline">security@traqo.com</a>.
            Privacy: <a href="mailto:privacy@verqo.com" className="underline">privacy@verqo.com</a>.
          </p>
        </div>

        <div className="mt-12 pt-8 border-t border-border text-[11px] text-foreground/45 leading-relaxed">
          v1.0 · Updated Feb 2026 · This document is a plain-English baseline drafted by verqo's engineering team and is being reviewed by outside counsel. Material updates will be re-presented for acknowledgement.
        </div>
      </div>
    </div>
  );
}
